import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BarChart3, RefreshCw, TrendingUp, Building2, FileText } from 'lucide-react';
import { toast } from 'react-toastify';
import { getConsolidatedPLReport } from '../../../api/reports';
import BlurredValue from '../../../components/common/BlurredValue';
import ReportPreviewModal from '../../../components/reports/ReportPreviewModal';
import { formatDateToISO } from '../../../utils/formatters';

const WholesaleConsolidatedPLReport = () => {
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  const now = new Date();
  const [dateRange, setDateRange] = useState({
    start: formatDateToISO(new Date(now.getFullYear(), now.getMonth(), 1)),
    end: formatDateToISO(new Date(now.getFullYear(), now.getMonth() + 1, 0))
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      // Pass module='wholesale' to filter data
      const data = await getConsolidatedPLReport(dateRange.start, dateRange.end, 'wholesale');
      setReport(data);
    } catch (error) {
      toast.error('Failed to load wholesale P&L report');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const netProfit = report?.summary?.net_profit || 0;
  const isProfit = netProfit >= 0;

  const getDateRangeLabel = () => {
    const start = new Date(dateRange.start).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
    const end = new Date(dateRange.end).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
    return `${start} - ${end}`;
  };

  const handleWhatsApp = async () => {
    if (!report) return;
    setSendingWhatsApp(true);
    try {
      const netProfitValue = report.summary?.net_profit || 0;
      const isProfitable = netProfitValue >= 0;
      const message = encodeURIComponent(
        `📊 *Wholesale Consolidated P&L Report*\n` +
          `📅 Period: ${getDateRangeLabel()}\n\n` +
          `💰 Wholesale Revenue: ${formatCurrency(report.summary?.total_revenue)}\n` +
          `📦 Wholesale COGS: ${formatCurrency(report.summary?.total_cogs)}\n` +
          `📈 Gross Profit: ${formatCurrency(report.summary?.gross_profit)}\n` +
          `${isProfitable ? '✅' : '❌'} Net ${isProfitable ? 'Profit' : 'Loss'}: ${formatCurrency(Math.abs(netProfitValue))}`
      );
      window.open(`https://wa.me/?text=${message}`, '_blank');
      toast.success('Opening WhatsApp...');
    } catch (error) {
      toast.error('Failed to open WhatsApp');
    } finally {
      setSendingWhatsApp(false);
    }
  };

  const ReportContent = () => {
    if (!report) return null;

    return (
      <div className="space-y-6">
        <h3 className="font-bold text-center text-xl text-gray-900 border-b pb-4">
          Wholesale Profit & Loss
        </h3>

        <table className="w-full text-sm border-collapse">
          <tbody>
            <tr className="bg-emerald-50">
              <td className="border border-gray-200 px-4 py-3 font-bold text-gray-900">
                Total Wholesale Revenue
              </td>
              <td className="border border-gray-200 px-4 py-3 text-right font-bold text-emerald-700">
                {formatCurrency(report.summary.total_revenue)}
              </td>
            </tr>
            <tr>
              <td className="border border-gray-200 px-4 py-2 pl-8 text-gray-600 italic">
                Less: Cost of Goods Sold
              </td>
              <td className="border border-gray-200 px-4 py-2 text-right text-red-600">
                ({formatCurrency(report.summary.total_cogs)})
              </td>
            </tr>
            <tr className="bg-gray-50">
              <td className="border border-gray-200 px-4 py-3 font-bold">Gross Profit</td>
              <td className="border border-gray-200 px-4 py-3 text-right font-bold text-gray-900">
                {formatCurrency(report.summary.gross_profit)}
              </td>
            </tr>
            <tr>
              <td className="border border-gray-200 px-4 py-2 pl-8 text-gray-600 italic">
                Less: Operating Expenses
              </td>
              <td className="border border-gray-200 px-4 py-2 text-right text-red-600">
                ({formatCurrency(report.summary.total_expenses)})
              </td>
            </tr>
            <tr className={isProfit ? 'bg-emerald-100' : 'bg-red-100'}>
              <td className="border border-gray-200 px-4 py-4 font-black text-lg">
                NET {isProfit ? 'PROFIT' : 'LOSS'}
              </td>
              <td
                className={`border border-gray-200 px-4 py-4 text-right font-black text-2xl ${isProfit ? 'text-emerald-800' : 'text-red-800'}`}
              >
                {formatCurrency(Math.abs(netProfit))}
              </td>
            </tr>
            <tr>
              <td className="border border-gray-200 px-4 py-2 text-gray-500 font-medium">
                Profit Margin
              </td>
              <td className="border border-gray-200 px-4 py-2 text-right font-black text-gray-700">
                {report.summary.profit_margin_percent?.toFixed(1)}%
              </td>
            </tr>
          </tbody>
        </table>

        <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 text-xs text-blue-800">
          <p className="font-bold mb-1 underline uppercase">Note on Wholesale Consolidation:</p>
          <p>
            This report includes all manufacturing company sales, wholesale distributor payments,
            and expenses marked with 'wholesale' module tag.
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6 border border-gray-100">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/wholesale/reports')}
                className="p-2 hover:bg-emerald-50 text-emerald-700 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Wholesale Consolidated P&L</h1>
                <p className="text-sm text-gray-500">
                  Integrated profit & loss statement for wholesale module
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center bg-gray-100 p-1 rounded-lg border border-gray-200">
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  className="bg-transparent px-2 py-1 text-sm outline-none"
                />
                <span className="px-1 text-gray-400">→</span>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  className="bg-transparent px-2 py-1 text-sm outline-none"
                />
              </div>
              <button
                onClick={fetchReport}
                className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600 transition-all shadow-sm"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
              {report && (
                <button
                  onClick={() => setShowReportModal(true)}
                  className="flex items-center gap-2 px-4 py-2 text-white font-semibold rounded-lg shadow-md transition-all hover:opacity-90"
                  style={{ backgroundColor: 'var(--wholesale-primary, #047857)' }}
                >
                  <FileText className="w-4 h-4" />
                  Preview
                </button>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center border border-gray-100">
            <div className="animate-spin w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-500 font-medium">Generating wholesale financial report...</p>
          </div>
        ) : report ? (
          <div className="space-y-6">
            {/* P&L Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-emerald-50 rounded-lg text-emerald-600">
                    <TrendingUp size={20} />
                  </div>
                  <span className="text-sm font-bold text-gray-500 uppercase tracking-wider">
                    Gross Revenue
                  </span>
                </div>
                <p className="text-3xl font-black text-gray-900">
                  <BlurredValue value={formatCurrency(report.summary.total_revenue)} />
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-red-50 rounded-lg text-red-600">
                    <BarChart3 size={20} />
                  </div>
                  <span className="text-sm font-bold text-gray-500 uppercase tracking-wider">
                    Total Expenses
                  </span>
                </div>
                <p className="text-3xl font-black text-gray-900">
                  <BlurredValue
                    value={formatCurrency(
                      report.summary.total_expenses + report.summary.total_cogs
                    )}
                  />
                </p>
              </div>

              <div
                className={`p-6 rounded-xl border shadow-sm ${isProfit ? 'bg-emerald-600 text-white border-emerald-700' : 'bg-red-600 text-white border-red-700'}`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <Building2 size={20} />
                  </div>
                  <span className="text-sm font-bold uppercase tracking-wider opacity-90">
                    Net {isProfit ? 'Profit' : 'Loss'}
                  </span>
                </div>
                <p className="text-3xl font-black">
                  <BlurredValue value={formatCurrency(Math.abs(netProfit))} />
                </p>
                <p className="text-xs mt-1 opacity-80 font-bold">
                  {report.summary.profit_margin_percent.toFixed(1)}% margin
                </p>
              </div>
            </div>

            {/* Detailed Statement */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-6 border-b border-gray-100 bg-gray-50/50">
                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-emerald-600" />
                  Financial Performance Breakdown
                </h3>
              </div>

              <div className="p-6 space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="font-bold text-gray-700 uppercase tracking-tight">
                    Wholesale Revenue
                  </span>
                  <span className="text-2xl font-black text-emerald-700">
                    <BlurredValue value={formatCurrency(report.summary.total_revenue)} />
                  </span>
                </div>

                <div className="space-y-2 pl-4">
                  <div className="flex justify-between items-center text-sm text-gray-500">
                    <span className="italic">Cost of Goods Sold (COGS)</span>
                    <span className="font-bold text-red-600">
                      <BlurredValue value={`(${formatCurrency(report.summary.total_cogs)})`} />
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm text-gray-500">
                    <span className="italic">Operating Expenses</span>
                    <span className="font-bold text-red-600">
                      <BlurredValue value={`(${formatCurrency(report.summary.total_expenses)})`} />
                    </span>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t-2 border-dashed border-gray-200">
                  <div
                    className={`flex justify-between items-center py-4 px-6 rounded-2xl ${isProfit ? 'bg-emerald-50 text-emerald-900' : 'bg-red-50 text-red-900'}`}
                  >
                    <span className="font-black text-xl">NET RESULT</span>
                    <span className="text-3xl font-black">
                      <BlurredValue value={formatCurrency(Math.abs(netProfit))} />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* Report Preview Modal */}
      <ReportPreviewModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        title="Wholesale P&L Report"
        subtitle={getDateRangeLabel()}
        onWhatsApp={handleWhatsApp}
        sendingWhatsApp={sendingWhatsApp}
      >
        <ReportContent />
      </ReportPreviewModal>
    </div>
  );
};

export default WholesaleConsolidatedPLReport;
